import { authenticatedFetch } from '../lib/api';

export type PushPlatform = 'ios' | 'android';

export interface PushRegistration {
  token: string;
  platform: PushPlatform;
}

const EXPO_PUSH_TOKEN = /^Expo(nent)?PushToken\[[^\]\s]+\]$/;

// The native shell posts {type:'PUSH_TOKEN', expoToken, deviceToken, platform}
// after each trusted page load. Only the Expo token is registered: the API
// sends through the Expo push service, and rejects anything else.
export const parsePushTokenMessage = (
  raw: unknown
): PushRegistration | null => {
  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

  const { type, expoToken, platform } = data as Record<string, unknown>;
  if (type !== 'PUSH_TOKEN') return null;
  if (typeof expoToken !== 'string' || !EXPO_PUSH_TOKEN.test(expoToken)) {
    return null;
  }
  if (platform !== 'ios' && platform !== 'android') return null;
  return { token: expoToken, platform };
};

let latest: PushRegistration | null = null;
let registeredKey: string | null = null;
let pendingKey: string | null = null;
const listeners = new Set<(push: PushRegistration) => void>();

const onMessage = (event: Event) => {
  if (typeof window === 'undefined' || !window.ReactNativeWebView) return;
  // The shell dispatches its messages with no source or origin. Anything that
  // has either (a frame, another window, or a script calling window.postMessage)
  // is not the shell, and must not be able to point this parent's pushes at another phone.
  const { source, origin, data } = event as MessageEvent;
  if (source || origin) return;

  const push = parsePushTokenMessage(data);
  if (!push) return;
  latest = push;
  listeners.forEach(listener => listener(push));
};

// Installed at import, before React renders: the shell posts on page load, so
// the message can land before anyone is logged in. Android WebViews deliver it
// on `document`, iOS on `window`.
if (typeof window !== 'undefined') {
  window.addEventListener('message', onMessage);
  document.addEventListener('message', onMessage);
}

export const getLatestPushToken = (): PushRegistration | null => latest;

export const subscribeToPushToken = (
  listener: (push: PushRegistration) => void
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Failure is silent and not retried here: the shell posts again on the next
// page load, which registers again.
export const registerPushToken = async (
  userId: string | number,
  push: PushRegistration
): Promise<void> => {
  const key = `${userId}:${push.token}`;
  if (registeredKey === key || pendingKey === key) return;

  pendingKey = key;
  try {
    await authenticatedFetch('/parent/device-tokens', {
      method: 'POST',
      body: JSON.stringify({ token: push.token, platform: push.platform }),
    });
    registeredKey = key;
  } catch {
    // Not marked registered, so the next page load tries again.
  } finally {
    if (pendingKey === key) pendingKey = null;
  }
};

// Call before the session is cleared: the request needs the bearer token, and
// keepalive lets it outlive the redirect that follows sign-out.
export const releasePushToken = (): void => {
  const push = latest;
  registeredKey = null;
  pendingKey = null;
  if (!push) return;

  authenticatedFetch('/parent/device-tokens/release', {
    method: 'POST',
    body: JSON.stringify({ token: push.token }),
    keepalive: true,
  }).catch(() => {
    // The API also hands the token to whoever signs in on this phone next.
  });
};
